import { Box, Typography } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { SupplierConfiguration } from "../../Create/components";
import { Action } from "./Action";
import newSupplierResolver from "../../../../../libs/form-validations/new-supplier";
import { Alert, Button } from "../../../../../shared";
import { SUPPLIER_TYPES, SUPPLIER_TYPE_OPTIONS } from '../../../../../interfaces';
import type { ISupplier } from "../../../../../interfaces";

interface SupplierDetailsProps {
    supplier: ISupplier;
    onUpdate: (data: Partial<ISupplier>) => Promise<void>;
    onDelete: () => Promise<void>;
    isUpdating: boolean;
    isDeleting: boolean;
    updateError?: unknown;
}

export function SupplierDetails({
    supplier,
    onUpdate,
    onDelete,
    isUpdating,
    isDeleting,
    updateError,
}: SupplierDetailsProps) {
    const navigate = useNavigate();

    const methods = useForm({
        resolver: newSupplierResolver,
        defaultValues: {
            Name: supplier.Name || "",
            Description: supplier.Description || "",
            Email: supplier.Email || "",
            PhoneNumber: supplier.PhoneNumber || "",
            Address: supplier.Address || "",
            City: supplier.City || "",
            Country: supplier.Country || "",
            Website: supplier.Website || "",
            LogoUrl: supplier.LogoUrl || "",
            Type: (() => {
                return SUPPLIER_TYPE_OPTIONS.find((t: any) => t.value === supplier.Type) || SUPPLIER_TYPE_OPTIONS[0];
            })(),
        },
        mode: "onChange",
    });

    const onSubmit = methods.handleSubmit(async (data) => {
        await onUpdate({
            Name: data.Name,
            Description: data.Description,
            Email: data.Email || undefined,
            PhoneNumber: data.PhoneNumber || undefined,
            Address: data.Address || undefined,
            City: data.City || undefined,
            Country: data.Country || undefined,
            Website: data.Website || undefined,
            LogoUrl: data.LogoUrl || undefined,
            Type: (data.Type as any)?.value || SUPPLIER_TYPES.MANUFACTURER,
        });
    });

    const isExistingMaterials =
        supplier.Type === SUPPLIER_TYPES.MANUFACTURER ||
        supplier.Type === SUPPLIER_TYPES.MANUFACTURER_AND_LABOR;
    const isExistingLabor =
        supplier.Type === SUPPLIER_TYPES.LABOR ||
        supplier.Type === SUPPLIER_TYPES.MANUFACTURER_AND_LABOR;

    const restrictedOptions = SUPPLIER_TYPE_OPTIONS.filter((option: any) => {
        // If they were materials, they can stay materials or move to both, but NOT labor
        if (isExistingMaterials) {
            return option.value === SUPPLIER_TYPES.MANUFACTURER || option.value === SUPPLIER_TYPES.MANUFACTURER_AND_LABOR;
        }
        // If they were labor, they can stay labor or move to both, but NOT materials
        if (isExistingLabor) {
            return option.value === SUPPLIER_TYPES.LABOR || option.value === SUPPLIER_TYPES.MANUFACTURER_AND_LABOR;
        }
        return true;
    });

    return (
        <Box component="div" width="100%" p={2} overflow="auto">
            <FormProvider {...methods}>
                <form onSubmit={onSubmit}>
                    <Box component="div" display="flex" flexDirection="column" maxWidth={800}>
                        <Box>
                            <Typography level="title-lg">Supplier Details</Typography>
                            <Typography level="body-sm">Update the supplier information below.</Typography>
                        </Box>

                        <SupplierConfiguration typeOptions={restrictedOptions} />

                        <Box display="flex" gap={2} justifyContent="flex-start" mt={3}>
                            <Button variant="outlined" color="neutral" onClick={() => navigate("/suppliers/list")}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isUpdating || !methods.formState.isDirty}
                                loading={isUpdating}
                                loadingPosition="start"
                                variant="soft"
                                color="primary"
                            >
                                Update Supplier
                            </Button>
                        </Box>

                        <Alert
                            successMessage={
                                !isUpdating && methods.formState.isSubmitSuccessful && "Supplier updated successfully!"
                            }
                            errorMessage={updateError}
                            sx={{ mt: 2 }}
                        />

                        <Action onDelete={onDelete} isDeleting={isDeleting} supplierName={supplier.Name} />
                    </Box>
                </form>
            </FormProvider>
        </Box>
    );
}
